import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, PlayCircle, X, PartyPopper } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { isSimple } from "@/lib/edition";

const SIMPLE_STEPS = ["settings", "contacts", "appointments"];

const ITEMS = [
  { key: "settings", to: "/impostazioni", title: "Completa dati aziendali e logo", how: "Impostazioni Brand → carica il logo e inserisci indirizzo e IBAN: compariranno su preventivi e fatture." },
  { key: "contacts", to: "/contatti", title: "Inserisci il primo contatto", how: "Contatti & Clienti → «Nuovo Contatto»: scegli Cliente o Fornitore e compila nome, telefono, email e P.IVA." },
  { key: "prices", to: "/listino", title: "Crea il tuo listino prezzi", how: "Listino Prezzi → aggiungi le lavorazioni che fai più spesso, con prezzo e unità di misura." },
  { key: "preziari", to: "/preziario", title: "Carica un preziario (facoltativo)", how: "Preziari → crea un preziario e importa il file Excel, PDF o CSV." },
  { key: "quotes", to: "/preventivi?new=1", title: "Crea il primo preventivo", how: "Preventivi → «Nuovo Preventivo»: scegli il cliente, aggiungi le voci e scarica il PDF o invialo su WhatsApp." },
  { key: "appointments", to: "/agenda", title: "Fissa un appuntamento", how: "Agenda → «Nuovo Appuntamento»: giorno, ora di inizio e fine, cliente e collaboratore." },
  { key: "expenses", to: "/spese", title: "Registra una spesa di gestione", how: "Spese di Gestione → «Nuova Spesa» oppure fotografa lo scontrino: i dati si compilano da soli." },
];

const Item = ({ it, done, onGo }) => (
  <li className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${done ? "opacity-60" : "hover:bg-secondary/60"}`} data-testid={`getting-started-item-${it.key}`}>
    {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" data-testid={`getting-started-done-${it.key}`} /> : <Circle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />}
    <div className="flex-1 min-w-0">
      <div className={`text-sm font-semibold ${done ? "line-through" : ""}`}>{it.title}</div>
      {!done && <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{it.how}</div>}
    </div>
    {!done && (
      <Button size="sm" variant="ghost" onClick={() => onGo(it.to)} className="shrink-0 text-primary" data-testid={`getting-started-go-${it.key}`}>
        Vai <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    )}
  </li>
);

export const GettingStarted = () => {
  const { user, setOnboarding } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/onboarding/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  if (!status) return null;

  const items = isSimple(user) ? ITEMS.filter((it) => SIMPLE_STEPS.includes(it.key)) : ITEMS;
  const doneCount = items.filter((it) => status[it.key]).length;
  const all = doneCount === items.length;
  return (
    <section className="rounded-xl border border-border bg-card p-5 sm:p-6 animate-in-up" data-testid="getting-started">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2">
            {all && <PartyPopper className="w-5 h-5 text-primary" />} {all ? "Ottimo, hai completato i primi passi!" : "Primi passi"}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">{all ? "Ora conosci tutte le funzioni principali di K Gestionale." : "Segui questi passaggi per impostare il gestionale. Puoi saltarli in qualsiasi momento."}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => window.dispatchEvent(new Event("start-tour"))} data-testid="getting-started-tour-button">
          <PlayCircle className="w-4 h-4 mr-1.5" /> Guida interattiva
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOnboarding({ checklist_hidden: true })} className="text-muted-foreground" data-testid="getting-started-hide-button">
          <X className="w-4 h-4 mr-1" /> {all ? "Chiudi" : "Salta"}
        </Button>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${(doneCount / items.length) * 100}%` }} />
        </div>
        <span className="text-xs font-semibold tabular-nums" data-testid="getting-started-progress">{doneCount}/{items.length}</span>
      </div>
      {!all && (
        <ul className="mt-3 grid md:grid-cols-2 gap-1">
          {items.map((it) => <Item key={it.key} it={it} done={!!status[it.key]} onGo={navigate} />)}
        </ul>
      )}
    </section>
  );
};

export default GettingStarted;
